import { APP_TOKENS, bigIntMax, DEFAULT_DECIMALS, PYUSD_TOKEN_ARB, scaleDown, scaleUp } from "logic/utils";
import { useEffect } from "react";
import { formatUnits } from "viem";

import { Loan } from "../data/types";
import { type LoanDetailsState, useLoanDetails } from "../LoanDetails/LoanDetailsProvider";
import { useLoansPage } from "../LoansPageProvider";

import { useBuyTx } from "./useBuyTx";

import { usePaymentQuote, useRepayTx } from ".";

export function useBridgeAndRepay(loan: Loan, customPaymentAmount?: bigint, isEnabled = true) {
  const { balances, reserveAccount, reservesRequired } = useLoanDetails();
  const { usdaiLocked } = useLoansPage();

  const loanChainId = loan.chainId;
  const pyusdDecimals = APP_TOKENS[loanChainId].pyusd.main.decimals;

  const paymentAmount = usePaymentQuote(loan, customPaymentAmount);

  const { flow, balancesKnown, deficit, scaledDownDeficit, depositAmount, depositToken } = resolveRepaymentFlow(
    balances,
    paymentAmount,
    pyusdDecimals,
    loanChainId,
    usdaiLocked ?? 0n,
    reservesRequired,
  );

  const transferAmount = flow === "NEED_USDAI_TRANSFER" ? deficit : undefined;
  const repayTx = useRepayTx(loan, customPaymentAmount, transferAmount, isEnabled);
  const isBalanceInsufficient = flow === "INSUFFICIENT_BALANCE";
  const needsBridgeFromBalances =
    flow === "NEED_DEPOSIT" || flow === "NEED_BRIDGE" || flow === "NEED_BRIDGE_AND_DEPOSIT";

  const bridgeTx = useBuyTx({
    depositAmountUnits: depositAmount,
    depositToken: depositToken,
    recipient: reserveAccount,
    isBalanceInsufficient,
    mainChainId: loanChainId,
    enabled: isEnabled && needsBridgeFromBalances,
  });

  const bridgeStarted = bridgeTx.steps.at(-1)?.status !== "idle";
  const needsBridge = needsBridgeFromBalances || bridgeStarted;

  const isBridgeInFlight = bridgeTx.isDone && flow !== "CAN_REPAY";

  const bridgeVars = [bridgeTx.action, bridgeTx.steps, bridgeTx.currentStepIndex] as const;
  const repayVars = [repayTx.action, repayTx.steps, repayTx.currentStepIndex] as const;
  const [action, steps, currentStepIndex] = needsBridge && !bridgeTx.isDone ? bridgeVars : repayVars;

  // DEBUG REPRO — repay button shows "Insufficient Balance" while UI shows enough.
  // Already shipped alongside this instrumentation:
  //   - useContractView.ts:48 — pass params.chainId to useRefetchOnBlockNumber
  //     (peer-chain ChainBalance was polling wallet-chain blocks).
  //   - useRepayTx.ts — quote deadline bucketed to a 30s wall-clock window
  //     instead of per-block block.timestamp, so the queryKey stops churning
  //     every ~8s. Restores the intended 30s refetchInterval and shrinks any
  //     paymentAmount drift between renders ~4x.
  // What this log distinguishes when isBalanceInsufficient flips true:
  //   - drift:               loanChainBalance unchanged, paymentAmount increased
  //   - account divergence:  borrowerEqualsConnected: false
  //   - staleness:           loanChainBalance decreased (dedupe assumption wrong)
  //   - decimal rounding:    raw bigints differ but UI prints them equal
  // Remove this useEffect once the root cause is confirmed.
  useEffect(() => {
    console.log("[debug useBridgeAndRepay]", {
      flow,
      balancesKnown,
      deficit: deficit.toString(),
      scaledDownDeficit: scaledDownDeficit.toString(),
      depositAmount: depositAmount.toString(),
      depositToken: depositToken.symbol,
      depositTokenChain: depositToken.chainId,
      paymentAmount: paymentAmount?.toString(),
      raUsdai: balances ? formatUnits(balances.reserve.usdaiMain, DEFAULT_DECIMALS) : "unknown",
      pyusdMain: balances ? formatUnits(balances.eoa.pyusdMain, pyusdDecimals) : "unknown",
      pyusdPeer: balances ? formatUnits(balances.eoa.pyusdPeer, pyusdDecimals) : "unknown",
      needsBridge,
      isBalanceInsufficient,
      isBridgeInFlight,
      reserveAccount,
      usdaiLocked: usdaiLocked?.toString(),
    });
  }, [
    flow,
    balancesKnown,
    deficit,
    scaledDownDeficit,
    depositAmount,
    depositToken,
    paymentAmount,
    balances,
    needsBridge,
    isBalanceInsufficient,
    isBridgeInFlight,
    reserveAccount,
    pyusdDecimals,
    usdaiLocked,
  ]);

  return {
    action,
    steps,
    currentStepIndex,

    needsBridge,
    isBalanceInsufficient: isBalanceInsufficient && !bridgeStarted,
    isBridgeInFlight,
    isLoadingBalances: !balancesKnown,

    flow,

    repayTx: {
      action: repayTx.action,
      steps: repayTx.steps,
      currentStepIndex: repayTx.currentStepIndex,
    },

    bridgeTx: {
      action: bridgeTx.action,
      steps: bridgeTx.steps,
      currentStepIndex: bridgeTx.currentStepIndex,
      isDone: bridgeTx.isDone,
    },
  };
}

type RepaymentFlow =
  | "CAN_REPAY"
  | "NEED_USDAI_TRANSFER"
  | "NEED_DEPOSIT"
  | "NEED_BRIDGE"
  | "NEED_BRIDGE_AND_DEPOSIT"
  | "INSUFFICIENT_BALANCE";

const UNRESOLVED = {
  flow: null as RepaymentFlow | null,
  balancesKnown: false,
  deficit: 0n,
  scaledDownDeficit: 0n,
  depositAmount: 0n,
  depositToken: PYUSD_TOKEN_ARB,
};

function resolveRepaymentFlow(
  balances: LoanDetailsState["balances"],
  paymentAmount: bigint | undefined,
  pyusdDecimals: number,
  loanChainId: number,
  dsraAmount: bigint,
  reservesRequired: bigint | undefined,
) {
  if (balances == null || paymentAmount == undefined || reservesRequired == undefined) return UNRESOLVED;

  // `required` is reserved for upcoming payments, so it can't fund this one —
  // counting it would understate the deficit and make repay() revert.
  const raUsdai = bigIntMax(balances.reserve.usdaiMain - reservesRequired, 0n);
  const eoaUsdai = balances.eoa.usdaiMain;
  const pyusdMain = balances.eoa.pyusdMain;
  const pyusdPeer = balances.eoa.pyusdPeer;

  // PYUSD deposits 1:1 as USDai — scale to 18 decimals for comparison
  const deficit =
    raUsdai >= paymentAmount ? 0n : scaleUp(paymentAmount, DEFAULT_DECIMALS) - scaleUp(raUsdai, DEFAULT_DECIMALS);

  const scaledPyusdMain = scaleUp(pyusdMain, pyusdDecimals);
  const scaledPyusdPeer = scaleUp(pyusdPeer, pyusdDecimals);
  const availableUsdai = eoaUsdai - dsraAmount;

  let flow: RepaymentFlow = "INSUFFICIENT_BALANCE";
  if (deficit === 0n) flow = "CAN_REPAY";
  else if (availableUsdai >= deficit) flow = "NEED_USDAI_TRANSFER";
  else if (scaledPyusdMain + availableUsdai >= deficit) flow = "NEED_DEPOSIT";
  else if (scaledPyusdPeer + availableUsdai >= deficit) flow = "NEED_BRIDGE";
  else if (scaledPyusdMain + scaledPyusdPeer + availableUsdai >= deficit) flow = "NEED_BRIDGE_AND_DEPOSIT";

  const scaledDownDeficit = scaleDown(deficit, pyusdDecimals, "up");
  let depositAmount = scaledDownDeficit;
  if (flow === "NEED_BRIDGE_AND_DEPOSIT") depositAmount = pyusdPeer;
  if (flow === "NEED_BRIDGE" && scaledDownDeficit > pyusdPeer) depositAmount = pyusdPeer;
  if (flow === "NEED_DEPOSIT" && scaledDownDeficit > pyusdMain) depositAmount = pyusdMain;

  const depositToken =
    flow === "NEED_DEPOSIT" ? APP_TOKENS[loanChainId].pyusd.main : APP_TOKENS[loanChainId].pyusd.peer;

  return { flow, balancesKnown: true, deficit, scaledDownDeficit, depositAmount, depositToken };
}
